import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Plus, Edit, Trash2, Users, X, Check, Minus, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import RoleService from '../../services/roleService';

const RolesPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const queryClient = useQueryClient();

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => RoleService.getRoles()
  });

  const { data: permissionsData } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => RoleService.getAvailablePermissions()
  });

  const createMutation = useMutation({
    mutationFn: RoleService.createRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setShowCreateModal(false);
      toast.success('Role created successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => RoleService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setEditingRole(null);
      toast.success('Role updated successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const deleteMutation = useMutation({
    mutationFn: RoleService.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      toast.success('Role deleted successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const toggleStatusMutation = useMutation({
    mutationFn: RoleService.toggleRoleStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      toast.success('Role status updated successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const roles = rolesData?.data?.roles || [];
  const permissions = permissionsData?.data?.permissions || {};

  const handleSubmit = (formData) => {
    console.log('Form data being sent:', formData);
    console.log('Permissions being sent:', formData.permissions);
    
    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleToggleStatus = (roleId) => {
    toggleStatusMutation.mutate(roleId);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="w-6 h-6 mr-2" />
            Roles & Permissions
          </h1>
          <p className="text-gray-600 mt-1">Manage user roles and their permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Role
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
              Loading roles...
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No roles found. Create your first role to get started.
                    </td>
                  </tr>
                ) : (
                  roles.map((role) => (
                    <tr key={role.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {role.display_name || role.name}
                            {RoleService.isStaffRole(role.name) && role.staff_level && (
                              <span className={`text-xs px-2 py-1 rounded-full ${RoleService.getStaffLevelBadgeColor(role.staff_level)}`}>
                                Level {role.staff_level}
                              </span>
                            )}
                            {RoleService.isStaffRole(role.name) && (
                              <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                                STAFF
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{role.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="w-4 h-4 mr-1" />
                          {role.users_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900 font-medium">
                            {role.permissions?.length || 0}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">permissions</span>
                          {/* Show escalation indicator for staff roles */}
                          {RoleService.isStaffRole(role.name) && role.permissions?.some(p => RoleService.isEscalationPermission(p)) && (
                            <span className="ml-2 bg-orange-100 text-orange-600 text-xs px-1 py-0.5 rounded">
                              ESC
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleStatus(role.id)}
                          className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                            role.is_active 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {role.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingRole(role)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="Edit role"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (role.users_count > 0) {
                                toast.error('Cannot delete role that is assigned to users');
                                return;
                              }
                              if (window.confirm(`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`)) {
                                deleteMutation.mutate(role.id);
                              }
                            }}
                            disabled={role.users_count > 0}
                            className={`p-1 rounded transition-colors ${
                              role.users_count > 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                            }`}
                            title={role.users_count > 0 ? 'Cannot delete - role has users' : 'Delete role'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showCreateModal || editingRole) && (
        <RoleModal
          role={editingRole}
          permissions={permissions}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRole(null);
          }}
          onSubmit={handleSubmit}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      )}
    </div>
  );
};

const RoleModal = ({ role, permissions, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    display_name: role?.display_name || '',
    description: role?.description || '',
    staff_level: role?.staff_level || 1,
    permissions: role?.permissions || [],
    is_active: role?.is_active !== false
  });

  const [errors, setErrors] = useState({});
  const [showStaffPermissionInfo, setShowStaffPermissionInfo] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate using RoleService
    const validation = RoleService.validateRoleData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      Object.values(validation.errors).forEach(error => toast.error(error));
      return;
    }

    setErrors({});
    
    // Auto-assign staff permissions based on level
    let submitData = { ...formData };
    
    // If it's a staff role and creating new (not editing), auto-assign permissions
    if (RoleService.isStaffRole(formData.name) && !role) {
      const defaultPermissions = RoleService.getDefaultStaffPermissions(formData.staff_level);
      // Merge with manually selected permissions
      const allPermissions = [...new Set([...formData.permissions, ...defaultPermissions])];
      submitData.permissions = allPermissions;
      
      toast.success(`Auto-assigned ${defaultPermissions.length} default permissions for Level ${formData.staff_level} staff`);
    }
    
    // Only include staff_level if role name contains "staff"
    if (!RoleService.isStaffRole(formData.name)) {
      delete submitData.staff_level;
    }

    onSubmit(submitData);
  };

  const handlePermissionToggle = (permissionName) => {
    const newPermissions = formData.permissions.includes(permissionName)
      ? formData.permissions.filter(p => p !== permissionName)
      : [...formData.permissions, permissionName];
    
    setFormData({ ...formData, permissions: newPermissions });
  };

  const organizedPermissions = RoleService.formatPermissions(permissions);
  const permissionCategories = RoleService.getPermissionCategories();

  const handleCategoryToggle = (category) => {
    const categoryPermissions = organizedPermissions[category]?.map(p => p.name) || [];
    const allSelected = categoryPermissions.every(name => formData.permissions.includes(name));
    
    let newPermissions;
    if (allSelected) {
      newPermissions = formData.permissions.filter(p => !categoryPermissions.includes(p));
    } else {
      newPermissions = [...new Set([...formData.permissions, ...categoryPermissions])];
    }
    
    setFormData({ ...formData, permissions: newPermissions });
  };

  const isCategoryFullySelected = (category) => {
    const categoryPermissions = organizedPermissions[category]?.map(p => p.name) || [];
    return categoryPermissions.length > 0 && categoryPermissions.every(name => formData.permissions.includes(name));
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = organizedPermissions[category]?.map(p => p.name) || [];
    const selected = categoryPermissions.filter(name => formData.permissions.includes(name));
    return selected.length > 0 && selected.length < categoryPermissions.length;
  };

  // Get default permissions for current staff level
  const getDefaultPermissionsForLevel = () => {
    if (!RoleService.isStaffRole(formData.name)) return [];
    return RoleService.getDefaultStaffPermissions(formData.staff_level);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-screen overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {role ? 'Edit Role' : 'Create New Role'}
              </h3>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Role Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Manager, Senior Developer, Staff Level 2"
                    required
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  <p className="mt-1 text-sm text-gray-500">
                    Use "staff" in the name to enable staff level selection and escalation capabilities.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.display_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Friendly name for display"
                  />
                  {errors.display_name && <p className="mt-1 text-sm text-red-600">{errors.display_name}</p>}
                </div>

                {/* Staff Level - Only show when role name contains "staff" */}
                {RoleService.isStaffRole(formData.name) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Staff Level *
                    </label>
                    <select
                      value={formData.staff_level}
                      onChange={(e) => setFormData({ ...formData, staff_level: parseInt(e.target.value) })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.staff_level ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    >
                      {RoleService.getStaffLevelOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.staff_level && <p className="mt-1 text-sm text-red-600">{errors.staff_level}</p>}
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => setShowStaffPermissionInfo(!showStaffPermissionInfo)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        View default permissions for this level
                      </button>
                      {showStaffPermissionInfo && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-medium text-blue-900 mb-2">
                            Default permissions for Level {formData.staff_level}:
                          </p>
                          <ul className="text-xs text-blue-700 space-y-1">
                            {getDefaultPermissionsForLevel().map(permission => (
                              <li key={permission} className="flex items-center gap-1">
                                <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                                {permission}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className={RoleService.isStaffRole(formData.name) ? '' : 'md:col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    rows={3}
                    placeholder="Brief description of this role's responsibilities"
                  />
                  {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Staff Role Alert */}
              {RoleService.isStaffRole(formData.name) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="font-medium text-blue-900 mb-2">Staff Role Detected</h5>
                      <p className="text-sm text-blue-700 mb-2">
                        This role contains "staff" in the name, so staff level selection is enabled. Staff roles have escalation capabilities and can be auto-assigned tickets.
                      </p>
                      {!role && ( // Only show for new roles
                        <div className="bg-blue-100 p-3 rounded-md">
                          <p className="text-sm text-blue-800 font-medium mb-1">
                            🎯 Level {formData.staff_level} Staff Features:
                          </p>
                          <ul className="text-xs text-blue-700 space-y-1">
                            {formData.staff_level >= 1 && <li>✓ Can respond to assigned tickets</li>}
                            {formData.staff_level >= 2 && <li>✓ Can escalate and forward tickets</li>}
                            {formData.staff_level >= 3 && <li>✓ Can assign tickets and mark as solved</li>}
                          </ul>
                          <p className="text-xs text-blue-600 mt-2 font-medium">
                            Default permissions will be automatically assigned on creation.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Permissions Section */}
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    Permissions ({formData.permissions.length} selected)
                  </h4>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const allPermissions = Object.values(organizedPermissions)
                          .flat()
                          .map(p => p.name);
                        setFormData({ ...formData, permissions: allPermissions });
                      }}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, permissions: [] })}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Permission Categories */}
                <div className="space-y-4">
                  {Object.entries(organizedPermissions).map(([category, categoryPermissions]) => (
                    <div key={category} className="border border-gray-200 rounded-lg">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleCategoryToggle(category)}
                              className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors ${
                                isCategoryFullySelected(category)
                                  ? 'bg-blue-600 border-blue-600'
                                  : isCategoryPartiallySelected(category)
                                  ? 'bg-blue-200 border-blue-400'
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {isCategoryFullySelected(category) && <Check className="w-3 h-3 text-white" />}
                              {isCategoryPartiallySelected(category) && <Minus className="w-3 h-3 text-blue-600" />}
                            </button>
                            <h5 className="font-medium text-gray-900 flex items-center gap-2">
                              {permissionCategories[category] || category}
                              {category === 'escalation' && (
                                <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded">
                                  STAFF ONLY
                                </span>
                              )}
                            </h5>
                          </div>
                          <span className="text-sm text-gray-500">
                            {categoryPermissions.filter(p => formData.permissions.includes(p.name)).length} / {categoryPermissions.length}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {categoryPermissions.map((permission) => (
                            <label 
                              key={permission.name} 
                              className={`flex items-center space-x-2 text-sm p-2 rounded transition-colors ${
                                permission.isEscalationRelated
                                  ? 'bg-orange-50 border border-orange-200 hover:bg-orange-100' 
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(permission.name)}
                                onChange={() => handlePermissionToggle(permission.name)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div>
                                <div className={`font-medium ${
                                  permission.isEscalationRelated
                                    ? 'text-orange-700'
                                    : 'text-gray-700'
                                }`}>
                                  {permission.displayName}
                                  {permission.isEscalationRelated && 
                                    <span className="ml-1 text-xs bg-orange-200 text-orange-800 px-1 rounded">ESC</span>
                                  }
                                </div>
                                {permission.description && (
                                  <div className="text-gray-500 text-xs">{permission.description}</div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {role ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;